import { SelectionMenuViewMode } from '@components/ButtonBaseWithSelectionMenu/types/buttonBaseWithSelectionMenu.types';
import Icon from '@components/v2/ui/Icon/Icon';
import Link, { LINK_HOVER_GROUP } from '@components/v2/ui/Link/Link';
import Tooltip from '@components/v2/ui/Tooltip/Tooltip';
import Typography from '@components/v2/ui/Typography/Typography';
import { Badge, Grid } from '@mui/material';
import classNames from 'classnames';
import { CircleAlert } from 'lucide-react';
import * as R from 'ramda';
import * as React from 'react';
import { useShallow } from 'zustand/react/shallow';
import ProductThumbnail from '../../../../../../components/Product/components/ProductThumbnail/ProductThumbnail';
import SelloutProductTag from '../../../../../../components/Product/product-cells/SelloutProductTag';
import SuggestedProductTag from '../../../../../../components/Product/product-cells/SuggestedProductTag';
import DynamicTagsAdapter from '../../../../../../components/tags/DynamicTags/DynamicTagsAdapter';
import useGeneralSettingsStore from '../../../../../../stores/GeneralSettings/useGeneralSettingsStore';
import useTeceoStyles from '../../../../../../useTeceoStyles';
import { OrderProductsTestIds } from '../../../../../../utils/testIds/OrderProducts.testIds';
import { MediaFormatEnum } from '../../../../../general-settings/enums/mediaFormat.enum';
import ProductCodesTooltip from '../../../../../products/components/ProductCodesTooltip';
import { formatProductCodes } from '../../../../../products/utils/productCodes.utils';
import { breakWordsByLetter } from '../../../../contexts/order.utils';
import { useOrderProductInfoStyles } from '../../styles/useOrderProductInfoStyles';
import OrderProductImageAndLabelTooltip from '../OrderProductImageAndLabelTooltip/OrderProductImageAndLabelTooltip';
import { OrderProductImageAndLabelProps } from './OrderProductImageAndLabel.interfaces';
import { useOrderProductPolicyViolations } from './hooks/useOrderProductPolicyViolations';

const OrderProductImageAndLabel = ({
  code,
  extraInfo,
  media,
  name,
  onClick,
  orderProductKeyIds,
  productErrors,
  referenceCode,
  srcMedia,
  viewMode,
  classifications,
  suggestedGrid,
  showSuggestedGrid,
  selloutSummary,
  showProductCodesTooltip,
  clickable = true,
}: OrderProductImageAndLabelProps) => {
  const { classes: teceoClasses } = useTeceoStyles();
  const { mediaFormat } = useGeneralSettingsStore(
    useShallow((state) => ({
      mediaFormat: state.generalSettings.mediaFormat,
    })),
  );
  const { classes } = useOrderProductInfoStyles({
    hasClassificationTags: !!classifications?.length,
    imageWidth: mediaFormat === MediaFormatEnum.SQUARE ? 55 : 40,
    showSuggestedGrid,
    clickable,
  });
  const { productBusinessPolicyViolations, openProductPolicyViolations } =
    useOrderProductPolicyViolations(orderProductKeyIds);
  const url = srcMedia || (media?.type !== 'VIDEO' ? media?.url : undefined);

  const codes = formatProductCodes({ code, referenceCode });
  const codesTitle = showProductCodesTooltip ? undefined : codes;

  const suggestedGridSum: number = React.useMemo(
    () => (suggestedGrid ? R.sum(suggestedGrid?.map((grid) => grid.orderItemQuantity || 0)) : 0),
    [suggestedGrid],
  );

  return (
    <div className={classNames(classes.colorOnHover, LINK_HOVER_GROUP)} onClick={onClick}>
      <div className={classes.productImage}>
        <Badge
          overlap="rectangular"
          className={classes.productImageErrorBadge}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ badge: { onClick: (event) => event.stopPropagation() } }}
          badgeContent={
            ((productErrors && productErrors.length > 0) ||
              productBusinessPolicyViolations.length > 0) &&
            viewMode === SelectionMenuViewMode.EDIT && (
              <Tooltip
                interactive
                TooltipContent={
                  <OrderProductImageAndLabelTooltip
                    productErrors={productErrors}
                    productBusinessPolicyViolations={productBusinessPolicyViolations}
                    onOpenDetails={openProductPolicyViolations}
                  />
                }
              >
                <div
                  className={classes.badgeIconWrapper}
                  test-id={OrderProductsTestIds.VALIDATION_ICON}
                  onClick={openProductPolicyViolations}
                >
                  <Icon icon={CircleAlert} size="sm" />
                </div>
              </Tooltip>
            )
          }
        >
          <ProductThumbnail url={url} />
        </Badge>
      </div>
      <div className={classes.itemInfo}>
        <Grid container>
          <Grid size={12}>
            <Typography
              testId={OrderProductsTestIds.PRODUCT_NAME}
              title={name}
              className={classNames(classes.productName, teceoClasses.semiBoldText)}
            >
              {clickable ? (
                <Link component="span" role="link" color="text.primary">
                  {breakWordsByLetter(name)}
                </Link>
              ) : (
                breakWordsByLetter(name)
              )}
            </Typography>

            <ProductCodesTooltip
              productCode={code}
              productReferenceCode={referenceCode}
              showTooltipContent={showProductCodesTooltip}
            >
              <Typography
                testId={OrderProductsTestIds.PRODUCT_CODE}
                color="textSecondary"
                title={codesTitle}
                className={classNames(classes.productCode, teceoClasses.semiBoldText)}
              >
                {breakWordsByLetter(codes)}
              </Typography>
            </ProductCodesTooltip>
          </Grid>

          {!!extraInfo && <Grid size={12}>{extraInfo}</Grid>}

          <Grid size={12}>
            <div className={classes.tagsContainer}>
              {showSuggestedGrid && (
                <div>
                  <SuggestedProductTag quantity={suggestedGridSum} />
                </div>
              )}

              {selloutSummary?.hasSelloutValues && (
                <div>
                  <SelloutProductTag quantity={selloutSummary.totalQuantitySold} />
                </div>
              )}
              {classifications && classifications.length > 0 && (
                <DynamicTagsAdapter tags={[...classifications]} />
              )}
            </div>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

export default React.memo(OrderProductImageAndLabel);
